
import React from 'react';
import { Loader } from 'lucide-react';
import { useLoading } from '@/contexts/LoadingContext';
import { LAYERS } from '@/utils/layers';

interface LoadingMessageProps {
  className?: string;
  variant?: 'toast' | 'overlay' | 'inline';
  showSpinner?: boolean;
}

export const LoadingMessage: React.FC<LoadingMessageProps> = ({
  className = '',
  variant = 'toast',
  showSpinner = true
}) => {
  const { loadingStates, isAnyLoading } = useLoading();

  console.log('🍞 [LoadingMessage] Render:', { 
    isAnyLoading, 
    loadingStatesCount: Object.keys(loadingStates).length,
    loadingStates: loadingStates 
  });

  if (!isAnyLoading) {
    console.log('🍞 [LoadingMessage] No loading states, returning null');
    return null;
  }

  // Get the most recent loading state (last added)
  const currentLoadingKey = Object.keys(loadingStates)[Object.keys(loadingStates).length - 1];
  const currentLoading = loadingStates[currentLoadingKey];

  console.log('🍞 [LoadingMessage] Showing loading:', { 
    currentLoadingKey, 
    currentLoading,
    variant 
  });

  const getVariantClasses = () => {
    switch (variant) {
      case 'toast':
        // PHASE 2: Improved Z-Index Management - Positioned higher to avoid toast overlap
        return `fixed bottom-20 right-4 bg-background border border-border rounded-lg shadow-lg px-4 py-3 z-[${LAYERS.TOAST}]`;
      case 'overlay':
        return `fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-[${LAYERS.TOAST}]`;
      case 'inline':
        return 'flex items-center gap-2 text-sm text-muted-foreground';
      default:
        return '';
    }
  };

  const getTypeColor = () => {
    switch (currentLoading.type) {
      case 'audio':
        return 'text-gold';
      case 'action':
        return 'text-blue-500';
      case 'data':
        return 'text-green-500';
      default:
        return 'text-foreground';
    }
  };

  const finalClassName = `${getVariantClasses()} ${className}`;
  console.log('🍞 [LoadingMessage] Final className:', finalClassName);
  console.log(`🍞 [LoadingMessage] Z-index should be z-[${LAYERS.TOAST}] positioned at bottom-20 right-4 to avoid toast overlap`);

  return (
    <div className={finalClassName}>
      <div className="flex items-center gap-3">
        {showSpinner && (
          <Loader className={`h-4 w-4 animate-spin ${getTypeColor()}`} />
        )}
        <span className={`font-medium ${getTypeColor()}`}>
          {currentLoading.message}
        </span>
      </div>
    </div>
  );
};
